import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { SITES, type KanjinSite } from '../kanjinData'
import type { SfxKind } from '../shared/Sfx'

interface Theme {
  site: KanjinSite
  top: string
  bottom: string
  amb: SfxKind
  silhouette: 'torii' | 'hall' | 'mound'
}

/** ⑦ ヴィネット球 — 建造物の「気配」を半球ドームで周囲に出現させ、中から木片が飛び出す */
export class Proto7 extends ProtoBase {
  readonly title = '7. ヴィネット球'
  readonly desc = '各地の気配（色・シルエット・環境音）の球に包まれ、木片が頭上を掠めて飛び出す'

  private themes: Theme[] = []
  private dome?: THREE.Mesh
  private idx = -1
  private phaseT = 0
  private chipFired = false
  private static readonly PHASE = 9

  protected start(): void {
    this.ctx.setWorld('void')
    this.ctx.view.setPose(new THREE.Vector3(0, 1.6, 0.01), new THREE.Vector3(0, 1.6, -2))

    const pick = (no: number) => SITES.find((s) => s.no === no)!
    this.themes = [
      { site: pick(42), top: '#31435c', bottom: '#c9a35a', amb: 'wave', silhouette: 'hall' },
      { site: pick(3), top: '#0b1410', bottom: '#233d2a', amb: 'wind', silhouette: 'torii' },
      { site: pick(16), top: '#1c1710', bottom: '#4c3a1c', amb: 'drum', silhouette: 'mound' },
      { site: pick(60), top: '#101c26', bottom: '#8a6a2a', amb: 'wave', silhouette: 'torii' },
    ]
    this.nextTheme()
  }

  private nextTheme(): void {
    this.idx = (this.idx + 1) % this.themes.length
    this.phaseT = 0
    this.chipFired = false
    const th = this.themes[this.idx]

    if (this.dome) {
      this.group.remove(this.dome)
      this.dome.geometry.dispose()
      ;(this.dome.material as THREE.Material).dispose()
    }
    const tex = Proto7.makeDomeTexture(th)
    const mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, transparent: true, opacity: 0 })
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(14, 32, 20), mat)
    this.dome.position.y = 0
    this.group.add(this.dome)

    this.ctx.sfx.play(th.amb)
    this.ctx.setCaption(`${th.site.building} — 「${th.site.piece}」\n${th.site.note}`)
  }

  update(dt: number): void {
    this.elapsed += dt
    this.phaseT += dt
    const mat = this.dome?.material as THREE.MeshBasicMaterial | undefined
    if (!mat) return

    // フェードイン→保持→アウト
    if (this.phaseT < 1.5) mat.opacity = this.phaseT / 1.5
    else if (this.phaseT > Proto7.PHASE - 1.2) mat.opacity = Math.max(0, (Proto7.PHASE - this.phaseT) / 1.2)
    else mat.opacity = 1
    this.dome!.rotation.y += dt * 0.02

    // 木片が正面奥から頭上を掠めて後方へ
    if (!this.chipFired && this.phaseT > 3.6) {
      this.chipFired = true
      const th = this.themes[this.idx]
      this.ctx.chips.launch({
        from: new THREE.Vector3(0, 2.5, -12),
        to: new THREE.Vector3(3, 14, 26),
        via: new THREE.Vector3(0.6, 2.3, 0.8),
        dur: 2.2,
        site: th.site,
        scale: 2.2,
      })
      setTimeout(() => this.ctx.sfx.play('whoosh'), 700)
    }

    if (this.phaseT >= Proto7.PHASE) this.nextTheme()
  }

  protected onDispose(): void {
    this.ctx.chips.clear()
  }

  private static makeDomeTexture(th: Theme): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = 1024
    c.height = 512
    const ctx = c.getContext('2d')!
    const g = ctx.createLinearGradient(0, 0, 0, 512)
    g.addColorStop(0, th.top)
    g.addColorStop(1, th.bottom)
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 1024, 512)

    // 星／光の粒
    for (let i = 0; i < 120; i++) {
      ctx.fillStyle = `rgba(255,240,200,${Math.random() * 0.35})`
      ctx.fillRect(Math.random() * 1024, Math.random() * 300, 2, 2)
    }

    // シルエット（正面=画像中央）
    ctx.fillStyle = 'rgba(5,6,8,0.92)'
    const cx = 512
    const base = 400
    if (th.silhouette === 'torii') {
      ctx.fillRect(cx - 90, base - 170, 22, 170)
      ctx.fillRect(cx + 68, base - 170, 22, 170)
      ctx.fillRect(cx - 130, base - 200, 260, 26)
      ctx.fillRect(cx - 104, base - 150, 208, 16)
    } else if (th.silhouette === 'hall') {
      ctx.fillRect(cx - 110, base - 110, 220, 110)
      ctx.beginPath()
      ctx.moveTo(cx - 150, base - 110)
      ctx.lineTo(cx, base - 185)
      ctx.lineTo(cx + 150, base - 110)
      ctx.closePath()
      ctx.fill()
    } else {
      ctx.beginPath()
      ctx.ellipse(cx, base, 190, 70, 0, Math.PI, 0)
      ctx.fill()
    }
    // 地面
    ctx.fillRect(0, base, 1024, 112)

    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return tex
  }
}
